import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';

import { Dialog } from 'components/common';
import { Input, Select } from 'components/form';
import { required } from 'components/form/validationRules';
import ChipsArrayInput from 'components/inputs/ChipsArrayInput';

import ImageInputField from '../levels/ImageInputField';
import { RewardBenefitDto, RewardLevelDto } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: theme.spacing(80),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  root: {
    maxWidth: '720px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  fieldsColumn: {
    flex: 1,
    minWidth: 200,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputNoMargin: {
    marginTop: 0,
  },
}));

interface EditBenefitsDialogProps {
  open: boolean;
  onClose: () => void;
  benefit?: RewardBenefitDto;
  onSaved?: (benefitId: string, levels: string[]) => void;
}

interface FormValues {
  icon?: File | string | null;
  title: string;
  description: string;
  systemType: string;
  levels: RewardLevelDto['id'][];
}

const getSystemTypeLabel = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  const hasUnderscore = value.includes('_');
  const normalized = hasUnderscore
    ? value.split('_').filter(Boolean).join(' ')
    : value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  return normalized.trim().toLowerCase();
};

const EditBenefitsDialog = ({ open, onClose, benefit, onSaved }: EditBenefitsDialogProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const [systemTypes, setSystemTypes] = useState<string[]>([]);
  const [levels, setLevels] = useState<RewardLevelDto[]>([]);

  useEffect(() => {
    let isMounted = true;

    const fetchSystemTypes = async () => {
      try {
        const { data } = await dataProvider.get('rewardsPerkSystemTypes');
        if (!isMounted) {
          return;
        }

        setSystemTypes(Array.isArray(data) ? data : []);
      } catch (error: any) {
        const message = error?.message || 'Failed to load perk system types';
        notify(message, 'warning');
      }
    };

    fetchSystemTypes();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify]);

  useEffect(() => {
    let isMounted = true;

    const fetchLevels = async () => {
      try {
        const { data } = await dataProvider.getList<RewardLevelDto>('rewardsLevel', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
        });

        if (!isMounted) {
          return;
        }

        setLevels(Array.isArray(data) ? data : []);
      } catch (error: any) {
        const message = error?.message || 'Failed to load reward levels';
        notify(message, 'warning');
      }
    };

    fetchLevels();

    return () => {
      isMounted = false;
    };
  }, [dataProvider, notify]);

  const initialValues: FormValues = useMemo(
    () => ({
      icon: benefit?.iconUrl ?? '',
      title: benefit?.title ?? '',
      description: benefit?.description ?? '',
      systemType: benefit?.systemType ?? '',
      levels: benefit?.levels ?? [],
    }),
    [benefit]
  );

  const systemTypeOptions = useMemo(
    () =>
      systemTypes.map((type) => ({
        value: type,
        label: getSystemTypeLabel(type),
      })),
    [systemTypes]
  );

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const formData = new FormData();

      formData.append('title', values.title?.trim() ?? '');
      formData.append('description', values.description?.trim() ?? '');
      formData.append('systemType', values.systemType ?? '');

      if (values.levels?.length) {
        values.levels.forEach((id) => {
          if (id) {
            formData.append('levels', id);
          }
        });
      } else {
        formData.append('levels', '');
      }

      if (values.icon instanceof File) {
        formData.append('icon', values.icon);
      } else if (typeof values.icon === 'string' && values.icon) {
        formData.append('icon', values.icon);
      }

      const response = benefit?.id
        ? await dataProvider.update('rewardsPerk', {
            id: benefit.id,
            data: formData,
          })
        : await dataProvider.post('rewardsPerk', {
            data: formData,
          });

      const responseData = (response as any)?.data as Partial<RewardBenefitDto> | undefined;
      const savedBenefitId = benefit?.id ?? responseData?.id;
      const savedLevels = (responseData?.levels ?? values.levels ?? []).filter((id): id is string =>
        Boolean(id)
      );
      if (savedBenefitId) {
        onSaved?.(savedBenefitId, savedLevels);
      }

      refresh();
      onClose();

      return response;
    } catch (error: any) {
      const message = error?.message || 'Failed to save rewards perk';
      notify(message, 'error');
      return { [FORM_ERROR]: message };
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Form<FormValues>
      key={benefit?.id ?? 'new-benefit'}
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <Dialog
            open={open}
            onCancel={onClose}
            title={benefit?.id ? 'Edit perk' : 'Add perk'}
            onConfirm={handleSubmit}
            confirmText="Save"
            submitting={submitting}
            error={submitError}
            classes={{ content: classes.content, root: classes.root }}>
            <div className={classes.layout}>
              <ImageInputField name="icon" />
              <div className={classes.fieldsColumn}>
                <Input
                  name="title"
                  label="Perk"
                  validate={required}
                  classes={{ input: classes.inputNoMargin }}
                />
                <Input
                  name="description"
                  label="Description"
                  validate={required}
                  inputProps={{ multiline: true, rows: 4 }}
                />
                <Select
                  name="systemType"
                  label="System Type"
                  options={systemTypeOptions}
                  disabled={!systemTypeOptions.length}
                />
                <Field
                  type="checkbox"
                  name="levels"
                  render={({ input }) => (
                    <ChipsArrayInput
                      label="Who gets this perk"
                      choices={levels}
                      optionValue="id"
                      optionLabel="name"
                      input={input}
                      showSelectAll={false}
                    />
                  )}
                />
              </div>
            </div>
          </Dialog>
        </form>
      )}
    />
  );
};

export default EditBenefitsDialog;
